/* eslint-disable no-param-reassign */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { getAdvertisementsQueryOptions, viewAdvertisementMutationFn } from '@/queries/advertisements';
import { Advertisement } from '@/queries/advertisements/types';
import { Habitat } from '@/queries/habitats/types';
import { Video } from '@/queries/media/types';
import { useSelector, useStore } from '@/redux/helper';
import { trackMP } from '@/shared/mp';

import { useSessionTimer } from './contexts';
import { advertisementWatched } from './redux/actions';
import { selectShouldPlayAdvertisement } from './redux/selectors';

export const useShouldPlayAdvertisement = () => useSelector(selectShouldPlayAdvertisement);

export const useFetchAdsForVideo = ({ _id: videoId }: Partial<Pick<Video, '_id'>> = {}) => {
  const store = useStore();
  // we only check if we need to play ads once per video source change
  // should we didn't use selector since it will be called once the condition is met
  const [shouldPlayAdvertisement, setShouldPlayAdvertisement] = useState(() =>
    selectShouldPlayAdvertisement(store.getState()),
  );

  const queryOptions = getAdvertisementsQueryOptions({ videoId });
  const enabled = Boolean(shouldPlayAdvertisement && queryOptions.enabled);
  const { data: advertisement, isFetching } = useQuery({ ...queryOptions, enabled, select: (data) => data.list?.[0] });

  const isAdsFetched = !enabled || !isFetching;

  // reset state when the video source changes
  useEffect(() => {
    setShouldPlayAdvertisement(selectShouldPlayAdvertisement(store.getState()));
  }, [store, videoId]);

  return { isAdsFetched, advertisement: enabled && isAdsFetched ? advertisement : undefined };
};

export const useFetchAdsForLiveStream = (
  { _id: habitatId, isHostStreamOn, isStreamOn }: Partial<Pick<Habitat, '_id' | 'isHostStreamOn' | 'isStreamOn'>> = {},
  streamKey: string | undefined,
) => {
  const sessionTimer = useSessionTimer();
  const queryOptions = getAdvertisementsQueryOptions({ habitatId });
  const shouldPlayAdvertisement = useShouldPlayAdvertisement();

  // if the iframe is in fullscreen or picture in picture mode, it cannot play ads
  // so we have to suppress ads if the iframe is not in normal mode
  const [shouldSuppressAds, setShouldSuppressAds] = useState(false);
  const isPictureInPictureMode = useRef(false);
  const isFullscreenMode = useRef(false);
  const nextAdCountdown = useSelector((state) => state.remoteConfig.advertisementConfig?.nextAdCountdown);

  const shouldEnableAds = !!(!isHostStreamOn && !!isStreamOn && shouldPlayAdvertisement && queryOptions.enabled);
  const enabled = shouldEnableAds && !shouldSuppressAds;
  const {
    data: advertisement,
    isFetching,
    isError,
  } = useQuery({ ...queryOptions, enabled, select: (data) => data.list?.[0] });
  const isAdsFetched = !enabled || !isFetching;

  useEffect(() => {
    if (isError) {
      // If fetching advertisement got error, we decrease the watch time by 60 seconds
      // to try again after one minute
      sessionTimer?.triggerNotificationAfter(60_000);
    }
  }, [isError, sessionTimer]);

  // Check the fullscreen and picture in picture state in iframe
  useEffect(() => {
    if (!streamKey) {
      setShouldSuppressAds(false);
      return undefined;
    }

    const checkShouldSuppressAds = () => {
      setShouldSuppressAds(isFullscreenMode.current || isPictureInPictureMode.current);
    };
    const handler = (event: MessageEvent) => {
      const { data: messageData } = event;
      const { key, value, streamId: id } = messageData;

      if (id === streamKey && key === 'fullscreen') {
        isFullscreenMode.current = value;
        checkShouldSuppressAds();
      }

      if (id === streamKey && key === 'pictureInPicture') {
        isPictureInPictureMode.current = value;
        checkShouldSuppressAds();
      }
    };
    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [streamKey]);

  // TEMP: send event to MP to track the ad suppression rate
  useEffect(() => {
    if (shouldEnableAds) {
      trackMP('should-show-live-stream-ad', {
        suppressed: shouldSuppressAds,
      });
      // mock next ad countdown to trigger skipped ad event
      if (shouldSuppressAds && nextAdCountdown) {
        const id = setInterval(() => {
          trackMP('should-show-live-stream-ad', {
            suppressed: true,
          });
        }, nextAdCountdown);
        return () => clearInterval(id);
      }
    }
    return undefined;
  }, [shouldEnableAds, shouldSuppressAds, nextAdCountdown]);

  return { isAdsFetched, advertisement: enabled && isAdsFetched ? advertisement : undefined };
};

export const useAdsPlayerState = (
  adsPlayer: HTMLVideoElement | undefined | null,
  {
    enabled,
    isAdsFetched,
    advertisement,
    onError,
  }: { enabled: boolean; isAdsFetched: boolean; advertisement: Advertisement | undefined; onError?: () => void },
) => {
  const dispatch = useDispatch();
  const [canPlayVideo, setCanPlayVideo] = useState(false);
  const hasAds = !!advertisement;

  const { mutate, isSuccess, reset } = useMutation({ mutationFn: viewAdvertisementMutationFn });

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // If the ads are fetched and there are no ads, we can play the video instead
  useEffect(() => {
    setCanPlayVideo(enabled ? (isAdsFetched ? !hasAds : false) : true);
  }, [isAdsFetched, hasAds, enabled]);

  // If there is ad, auto play it
  useEffect(() => {
    if (!adsPlayer || canPlayVideo) {
      return undefined;
    }

    const handler = () => {
      adsPlayer.play().catch(() => {
        adsPlayer.muted = true;
        adsPlayer.play();
      });
    };

    adsPlayer.addEventListener('loadedmetadata', handler);

    return () => {
      adsPlayer.removeEventListener('loadedmetadata', handler);
    };
  }, [adsPlayer, canPlayVideo, advertisement?._id, mutate]);

  useEffect(() => {
    if (!adsPlayer || canPlayVideo || isSuccess) {
      return undefined;
    }

    const handler = () => {
      if (advertisement?._id) {
        mutate(advertisement._id);
      }
    };

    adsPlayer.addEventListener('play', handler);

    return () => {
      adsPlayer.removeEventListener('play', handler);
    };
  }, [advertisement?._id, mutate, isSuccess, canPlayVideo, adsPlayer]);

  // If there is an error, we skip the ad and play the video
  useEffect(() => {
    if (!adsPlayer || canPlayVideo) {
      return undefined;
    }

    const handler = () => {
      setCanPlayVideo(true);
      onErrorRef.current?.();
    };

    adsPlayer.addEventListener('error', handler);

    return () => {
      adsPlayer.removeEventListener('error', handler);
    };
  }, [adsPlayer, canPlayVideo]);

  // If the ad is played to the end, we can play the video
  useEffect(() => {
    if (!adsPlayer || canPlayVideo) {
      return undefined;
    }

    const handler = () => {
      setCanPlayVideo(true);
      dispatch(advertisementWatched());
    };

    adsPlayer.addEventListener('ended', handler);

    return () => {
      adsPlayer.removeEventListener('ended', handler);
    };
  }, [adsPlayer, canPlayVideo, dispatch]);

  // Play the ad when the tab is visible if the ad is appearing when the tab is hidden
  useEffect(() => {
    if (!adsPlayer || canPlayVideo) {
      return undefined;
    }
    const handler = () => {
      adsPlayer.play();
    };
    document.addEventListener('visibilitychange', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
    };
  }, [adsPlayer, canPlayVideo]);

  // Reset the mutation when the new advertisement is fetched
  useEffect(() => {
    if (isAdsFetched) {
      reset();
    }
  }, [reset, isAdsFetched]);

  return { canPlayVideo };
};

export const useAdsPlayerStateForVideo = (
  adsPlayer: HTMLVideoElement | undefined | null,
  video: Partial<Pick<Video, '_id'>> | undefined,
) => {
  const { isAdsFetched, advertisement } = useFetchAdsForVideo(video);
  const state = useAdsPlayerState(adsPlayer, { enabled: !!video, isAdsFetched, advertisement });

  return {
    ...state,
    isAdsFetched,
    advertisement,
  };
};

export const useAdsPlayerStateForLiveStream = (
  adsPlayer: HTMLVideoElement | undefined | null,
  habitat: Partial<Pick<Habitat, '_id' | 'isHostStreamOn' | 'isStreamOn'>> | undefined,
  streamKey: string | undefined,
) => {
  const sessionTimer = useSessionTimer();
  const { isAdsFetched, advertisement } = useFetchAdsForLiveStream(habitat, streamKey);
  const state = useAdsPlayerState(adsPlayer, {
    enabled: !!habitat,
    isAdsFetched,
    advertisement,
    onError: () => {
      sessionTimer?.triggerNotificationAfter(60_000);
    },
  });

  return {
    ...state,
    isAdsFetched,
    advertisement,
  };
};
